import React, { useState, useEffect, useRef } from 'react'
import { useMediaQuery } from 'react-responsive'
import axios from 'axios'

import Grid from '../grid'
import { Input, Select, TextArea, Checkbox } from '../formElements'
import DecorativeButton from '../decorativeButton'

import { StyledForm, StyledPot, ButtonBox, Modal, ModalInner } from './styles'
import { devicesMax } from './../../styles/breakpoints'
import { AnimatePresence } from 'framer-motion'
import { Text } from '../textElements'
import DecorativeHeading from '../decorativeHeading'

const Form = ({ caption, onSuccess, getValues }) => {
  const isMaxTabletSm = useMediaQuery({ query: devicesMax.mobile.lg })

  const [values, setValues] = useState({
    uuid: { value: 'B88CF84B-EDFB-6B15-D587-B787E6F394B0', valid: true },
    campaign_code: { value: 'FOREMYCNY2020GOODTIMESGONE ', valid: true },
    fb_caption: { value: caption, valid: true },
  })

  const [isDisabled, setIsDisabled] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)

  const formRef = useRef(null)

  const constraints = {
    firstname: { required: true },
    lastname: { required: true },
    age: { required: true, numeric: true },
    gender: { required: true },
    email: { required: true, email: true },
    contactno: { required: true, phone: true },
    address_1: { required: true },
    pdpa: { required: true },
    online_reference: { required: true },
  }

  const checkIfValid = name => (values[name] ? values[name].valid : true)

  const checkIfEmail = email => {
    const re = /\S+@\S+\.\S+/
    return re.test(email)
  }

  const checkIfPhoneNumber = phone => {
    const re = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{2,8}$/im
    return re.test(phone)
  }

  const checkIfNumber = number => {
    const re = /^[0-9]*$/im
    return re.test(number)
  }

  const validateField = (name, value = '') => {
    let valid = false
    const constraint = constraints[name]

    if (constraint.required && !value) valid = false
    else if (constraint.numeric) valid = checkIfNumber(value)
    else if (constraint.email) valid = checkIfEmail(value)
    else if (constraint.phone) valid = checkIfPhoneNumber(value)
    else valid = true

    return valid
  }

  const onChange = e => {
    const value = e.target.type === 'checkbox' ? +e.target.checked : e.target.value
    const name = e.target.getAttribute('name')

    const valid = validateField(name, value)

    setValues({
      ...values,
      [name]: {
        value,
        valid,
      },
    })
  }

  const onSubmit = e => {
    e.preventDefault()
    if (isDisabled || isSubmitting) return

    const currentValues = {}
    let isAllValid = true

    const allFields = formRef.current.querySelectorAll('input:not([name="person"]), textarea, select')

    allFields.forEach(field => {
      const name = field.name
      const value = field.type === 'checkbox' ? +field.checked : field.value
      const valid = validateField(name, value)

      if (!valid) isAllValid = false

      currentValues[name] = {
        value,
        valid,
      }
    })

    setValues({ ...values, ...currentValues })
    if (isAllValid) setIsSubmitting(true)
  }

  useEffect(() => {
    if (!isSubmitting) return

    const formData = new FormData()
    Object.keys(values).forEach(field => formData.set(field, values[field].value))

    if (onSuccess) onSuccess(values)

    axios
      .post('https://sm-admin.forefront.international/server/api/registration', formData)
      .then(e => {
        setIsSubmitting(false)

        if (e.data.error) {
          console.error(e)
          return
        }

        setIsSubmitted(true)
        if (onSuccess) onSuccess(values)
      })
      .catch(e => {
        setIsSubmitting(false)
        console.error(e)
      })
  }, [isSubmitting])

  useEffect(() => {
    if (getValues) getValues(values)
  }, [values])

  return (
    <div>
      <StyledForm onSubmit={onSubmit} ref={formRef}>
        <Grid columns={isMaxTabletSm ? 1 : 2} gap="1rem">
          <div>
            <StyledPot type="text" name="person" onChange={() => setIsDisabled(true)} />
            <Input type="text" name="firstname" onChange={onChange} isValid={checkIfValid('firstname')}>
              First Name
            </Input>
            <Input type="text" name="lastname" onChange={onChange} isValid={checkIfValid('lastname')}>
              Last Name
            </Input>
            <Grid columns="40% auto" gap="1rem">
              <Input name="age" onChange={onChange} isValid={checkIfValid('age')}>
                Age
              </Input>
              <Select
                name="gender"
                options={[
                  { label: 'Male', value: 'male' },
                  { label: 'Female', value: 'female' },
                ]}
                onChange={onChange}
                isValid={checkIfValid('gender')}
              >
                Gender
              </Select>
            </Grid>
            <Input type="email" name="email" onChange={onChange} isValid={checkIfValid('email')}>
              Email Address
            </Input>
          </div>

          <div>
            <Input type="phone" name="contactno" onChange={onChange} isValid={checkIfValid('contactno')}>
              Contact Number
            </Input>
            <Input
              type="text"
              name="online_reference"
              onChange={onChange}
              isValid={checkIfValid('online_reference')}
            >
              Facebook Profile URL
            </Input>
            <TextArea name="address_1" rows="4" onChange={onChange} isValid={checkIfValid('address_1')}>
              Mailing Address
            </TextArea>
            <Checkbox name="pdpa" id="pdpa" onChange={onChange} isValid={checkIfValid('pdpa')}>
              I have read and agreed with the{' '}
              <a href="#" onClick={() => setModalOpen(true)}>
                Terms and Conditions
              </a>{' '}
              and{' '}
              <a href="https://forefront.international/pdpa/" target="_blank" rel="noopener noreferrer">
                PDPA Consent Clause
              </a>
              .
            </Checkbox>
            <ButtonBox>
              <DecorativeButton>Continue</DecorativeButton>
            </ButtonBox>
          </div>
        </Grid>
      </StyledForm>

      <AnimatePresence>
        {modalOpen && (
          <Modal animate={{ opacity: [0, 1] }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
            <ModalInner>
              <DecorativeHeading>Terms & Conditions</DecorativeHeading>
              <ol style={{ textAlign: 'left' }}>
                <li>
                  <Text>This giveaway runs from 2 - 12 January 2020.</Text>
                </li>
                <li>
                  <Text>
                    Participants are to complete the “To Good Times Gone By” questionnaire and share their
                    obtained result on Facebook with the hashtag #ForeGoodTimes.
                  </Text>
                </li>
                <li>
                  <Text>
                    60 winners will be selected by FOREFRONT and will be contacted after the giveaway period.
                  </Text>
                </li>
                <li>
                  <Text>Each winner will receive one (1) set of FOREFRONT 2020 Ang Pows.</Text>
                </li>
                <li>
                  <Text>
                    Participants are only eligible to win if all compulsory instructions have been followed.
                  </Text>
                </li>
                <li>
                  <Text>
                    By participating in this giveaway, you accept all terms and conditions. Information
                    submitted in connection to the giveaway will be treated in accordance with our Privacy
                    Policy
                  </Text>
                </li>
                <li>
                  <Text>
                    The FOREFRONT 2020 Ang Pows are only eligible for shipping within Malaysia, and cannot be
                    redeemed for cash.
                  </Text>
                </li>
                <li>
                  <Text>
                    FOREFRONT reserves the right to change these terms and conditions at any time without
                    prior notice.
                  </Text>
                </li>
              </ol>
              <DecorativeButton onClick={() => setModalOpen(false)}>CLOSE</DecorativeButton>
            </ModalInner>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Form
